import { ReactMarkdown } from 'react-markdown/lib/react-markdown';
import Accordion from '@/components/elements/Accordion';
import { AccordionContent, AccordionItem, AccordionTrigger } from '@/components/elements/Accordion/Accordion';
import Button from '@/components/elements/Button';
import { LinkRenderer } from '@/components/elements/Link';
import Container from '@/components/global/Container';
import { TitleAndDescription } from '@/components/shared/TitleAndDescription';
import { IComponentBlocksFaq } from '@/graphql/__generated__/global/types';
import { TRACKING_EVENT, track } from '@/lib/gtm';
import { micromark } from 'micromark';
import { FAQPageJsonLd } from 'next-seo';
import { useRouter } from 'next/router';

export const BlocksFaq = ({ id, titleDescription, faqs, ctaDescription, ctaButton }: IComponentBlocksFaq) => {
    const route = useRouter();
    return (
        <>
            <Container bg="sand" rounded>
                {titleDescription && (
                    <TitleAndDescription
                        id={id ?? `${titleDescription?.title}_${titleDescription?.description}`}
                        title={titleDescription?.title}
                        headingLevel={titleDescription?.headingLevel}
                        description={titleDescription?.description}
                    />
                )}

                {faqs?.data && faqs.data.length > 0 && (
                    <Accordion type="single" collapsible className="w-full h-full grid items-center gap-5">
                        {faqs.data.map((faq, index) => (
                            <AccordionItem
                                value={faq.id ?? faq.attributes?.question ?? `item-${index}`}
                                key={faq.id ?? faq.attributes?.question}
                                className="h-full flex flex-col items-start justify-center border-primary border"
                            >
                                <AccordionTrigger
                                    className="p large text-left"
                                    onClick={() =>
                                        track(TRACKING_EVENT.TOGGLED_FAQ, {
                                            faqQuestion: faq.attributes?.question ?? '',
                                            page: route.asPath,
                                        })
                                    }
                                >
                                    {faq.attributes?.question}
                                </AccordionTrigger>
                                <AccordionContent>
                                    <ReactMarkdown
                                        className="prose max-w-full"
                                        components={{
                                            p: ({ ...props }) => <p className="small" {...props} />,
                                            li: ({ ...props }) => <li className="small" {...props} />,
                                        }}
                                    >
                                        {faq.attributes?.answer as string}
                                    </ReactMarkdown>
                                </AccordionContent>
                            </AccordionItem>
                        ))}
                    </Accordion>
                )}
                {(ctaDescription || ctaButton) && (
                    <div className="flex flex-col justify-center items-center gap-6 mt-[4.5rem]">
                        {ctaDescription && <p className="h5">{ctaDescription}</p>}
                        {ctaButton && (
                            <Button
                                variant="outline"
                                colour="primary"
                                asChild
                                onClick={() => {
                                    track(TRACKING_EVENT.PRESSED_BUTTON, {
                                        label: ctaButton?.label ?? '',
                                        page: route.asPath,
                                    });
                                }}
                            >
                                <LinkRenderer {...ctaButton}>{ctaButton?.label}</LinkRenderer>
                            </Button>
                        )}
                    </div>
                )}
            </Container>
            <FAQPageJsonLd
                mainEntity={[
                    ...(faqs?.data || []).map((faq) => ({
                        questionName: faq.attributes?.question || '',
                        acceptedAnswerText: micromark(faq.attributes?.answer ?? ''),
                    })),
                ]}
            />
        </>
    );
};

export default BlocksFaq;
